'use client';

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { signIn } from './auth-client';
import { Button } from '@/platform/ui/button';
import { Input } from '@/platform/ui/input';
import { Label } from '@/platform/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/platform/ui/card';
import { Alert, AlertDescription } from '@/platform/ui/alert';

// Turns a sign-in failure into something a person can act on. Wrong-password
// style messages from Supabase are already clear, so they pass through. Server
// faults (HTTP 5xx) and empty or JSON-looking messages (e.g. "{}") get a plain
// explanation instead.
function friendlySignInError(err: any): string {
  const message = typeof err?.message === 'string' ? err.message.trim() : '';
  const status = typeof err?.status === 'number' ? err.status : 0;
  const looksBroken = !message || /^[\[{]/.test(message);

  if (status >= 500 || looksBroken) {
    return "We couldn't sign you in because of a problem on our side. Please try again in a moment, and let us know if it keeps happening.";
  }
  return message;
}

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const prefillEmail = searchParams.get('email') || '';
  const redirectUrl = searchParams.get('redirect') || '';
  const surveyToken = searchParams.get('survey_token') || '';
  const isSurveyFlow = !!surveyToken;

  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      if (redirectUrl) {
        router.push(redirectUrl);
      } else {
        router.push('/dashboard');
      }
    } catch (err: any) {
      console.error('[LoginForm] Sign in error:', err);
      setError(friendlySignInError(err));
      setLoading(false);
    }
  };

  const buildSignupUrl = () => {
    const params = new URLSearchParams();
    if (prefillEmail) params.set('email', prefillEmail);
    if (redirectUrl) params.set('redirect', redirectUrl);
    if (surveyToken) params.set('survey_token', surveyToken);
    const qs = params.toString();
    return `/auth/signup${qs ? `?${qs}` : ''}`;
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{isSurveyFlow ? 'Sign in to continue' : 'Sign In'}</CardTitle>
        <CardDescription>
          {isSurveyFlow
            ? 'Sign in or create an account to start your survey'
            : 'Enter your credentials to access your account'}
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="pr-10"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
          <Button
            type="button"
            variant="link"
            className="w-full"
            onClick={() => router.push(buildSignupUrl())}
          >
            Don't have an account? Sign up
          </Button>
          <Button
            type="button"
            variant="link"
            className="w-full"
            onClick={() => router.push('/auth/forgot-password')}
          >
            Forgot password?
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
